/**
 * Linked tab — lists conversations linked to this one (KC2 "Linked" tab) and
 * offers a "Link conversation" action that opens the reusable search drawer for
 * a multi-select link. Fetches GET /conversations/:id/linked.
 */
import { useEffect, useState, type JSX } from 'react'
import { ChevronRight, Link2 } from 'lucide-react'
import { api } from '../../../api/client.js'
import { R } from './primitives.js'
import { formatCustomerName } from '../../../utils/formatCustomerName.js'

interface LinkedTabProps {
  conversationId: string
  /** Bumped by the parent whenever a link is added, to force a refetch. */
  refreshKey?: number | undefined
  onNavigateToConversation: (conversationId: string) => void
  onLink: () => void
}

interface LinkedConversation {
  id: string
  referenceId: string
  subject: string | null
  status: string
  openedAt: string | null
  displayName: string | null
  primaryHandle: string | null
}

export default function LinkedTab({
  conversationId,
  refreshKey,
  onNavigateToConversation,
  onLink,
}: LinkedTabProps): JSX.Element {
  const [items, setItems] = useState<LinkedConversation[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api.get<{ items: LinkedConversation[] }>(`/conversations/${conversationId}/linked`)
      .then((res) => { if (!cancelled) setItems(res.items || []) })
      .catch(() => { if (!cancelled) setItems([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [conversationId, refreshKey])

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-5">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">
          Linked Conversations {items.length > 0 && <span className="text-slate-400">({items.length})</span>}
        </h3>
        <button
          type="button"
          onClick={onLink}
          className="flex items-center gap-1.5 rounded-[3px] bg-[#3366FF] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#2656cc]"
        >
          <Link2 className="h-4 w-4" />
          Link
        </button>
      </div>

      {loading ? (
        <div className="py-4 text-center text-sm text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className={`flex flex-col items-center justify-center gap-2 ${R} border border-dashed border-slate-200 bg-white py-12 text-center`}>
          <Link2 className="h-6 w-6 text-slate-300" />
          <p className="text-sm text-slate-400">No linked conversations yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div
              key={c.id}
              className={`${R} cursor-pointer border border-slate-200 bg-white p-4 hover:border-[#3366FF] hover:bg-blue-50`}
              onClick={() => onNavigateToConversation(c.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-slate-400" />
                    <span className="font-mono text-sm font-medium text-slate-800">#{c.referenceId}</span>
                    <span className={`rounded-[3px] px-2 py-0.5 text-[10px] font-semibold uppercase ${
                      c.status === 'open' ? 'bg-green-100 text-green-700' :
                      c.status === 'closed' ? 'bg-slate-100 text-slate-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {c.status}
                    </span>
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-800">{c.subject || '(No subject)'}</div>
                  <div className="mt-1 text-xs text-slate-500">{formatCustomerName(c.displayName, c.primaryHandle)}</div>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
